import type {
  AuditLogData,
  AuditLogDataItem,
  AuditLogDetailsRequestBody,
  AuditLogDetailsResponse,
  AuditLogsRequestBody,
  AuditLogsResponse,
  NotificationLogData,
  NotificationLogParameters,
  NotificationLogResponse,
} from '../../@types/audit';
import type {
  BackgroundJobDetailsResult,
  BackgroundJobsApiProps,
  BackgroundJobsItem,
  BackgroundJobsResult,
  CloneRecordsJobResult,
  CreateBackgroundJobResult,
  CreateExportJobResult,
  CreateImportJobResult,
  ExportRecordFieldJobResult,
  ExportSingleRecordJobResult,
  GetAnalysisJobStatusApiResponse,
  MutateRecordBackgroundJobResult,
  StartAnalysisJobApiArg,
  StartAnalysisJobApiResponse,
  StartImportJobApiProps,
} from '../../@types/background-jobs';
import type { FieldFilters, ResultValidationResponse } from '../../@types/common';
import type { GetAllFormsApiProps } from '../../@types/forms';
import type {
  GetLinkedRecordColumnValuesApiProps,
  LinkedRecordResult,
  LinkedRecordsApiProps,
  LinkedRecordsWrapper,
  SaveLayoutApiProps,
} from '../../@types/linked-records-field';
import type {
  AssignedRecordItem,
  AssignedRecordsCount,
  AssignedRecordsCountResponse,
  AssignedRecordsResponse,
  AssignedRecordsResult,
  BackgroundJobRecordUpdateApiProps,
  CloneRecordsApiProps,
  DeleteRecordResponse,
  ExportAllFormsRecords,
  ExportFullRecordsDataProps,
  ExportRecord,
  ExportRecordArgs,
  ExportRecordFieldApiProps,
  ExportRecordResponse,
  ExportRecords,
  ExportSingleRecord,
  GetRecordByIdApiProps,
  GetRecordsApiProps,
  InvitationEmailResult,
  LinkedRecordDefaultValuesApiProps,
  MyActivitiesApiProps,
  MyActivitiesApiResponse,
  PatchRecordApiProps,
  PatchRecordSuccessResponse,
  PostRecordApiProps,
  PostRecordSuccessResponse,
  RecordCollaborators,
  RecordCollaboratorsWrapper,
  RecordCountApiProps,
  RecordDefaultValuesWrapper,
  RecordFieldTemplateExport,
  RecordFieldVisibilityApiArgs,
  RecordPermissionsResponse,
  RecordPermissionsResult,
  RecordResult,
  RecordSectionVisibilityApiArgs,
  RecordsResult,
  RecordStructure,
  RecordsWrapper,
} from '../../@types/records';
import type { SummaryRecordLinkResponse, SummaryRecordLinkResult } from '../../@types/summary-record-link-field';
import type { EmailThisTemplate } from '../../components/fields/email-this-button/email-this-modal.component';
import type { CollaboratorsRequestBody } from '../../components/fields/invitation-field/invitation-field.component';
import type {
  ExternalEmailInvitation,
  ExternalUserEmailTemplate,
} from '../../components/fields/invite-button-field/invite-modal.component';
import {
  createRecordData,
  fileToSrc,
  transformAuditLogDetailsResponse,
  transformAuditLogResponse,
} from '../../utils/data-transformation-utils';
import {
  createFieldFilterText,
  createFieldFilterTextAsFirstQueryParameter,
  createSummaryLinkedRecordsFilterText,
  exportRecordURLConstruct,
} from '../../utils/filter-url-builder-utils';
import { createFieldSortText } from '../../utils/sort-url-builder-utils';
import { forceAssert } from '../../utils/type-guards';
import { providesList } from '../util/api-util';
import { api } from './api';

export type GetExternalUserEmailTemplateArgs = { type?: string };

export const recordApi = api
  .enhanceEndpoints({
    addTagTypes: [
      'Record',
      'DefaultRecord',
      'LinkedRecord',
      'ReverseLinkedRecord',
      'SummaryLinkedRecord',
      'TotalRecordCount',
      'Collaborator',
      'AllFormsRecord',
      'AssignedRecords',
      'AssignedRecordsCount',
      'RecordVersion',
      'RecordPermissions',
      'BackgroundJobs',
    ],
  })
  .injectEndpoints({
    overrideExisting: 'throw',
    endpoints: (builder) => ({
      getRecordById: builder.query<RecordStructure, GetRecordByIdApiProps>({
        query: (args: { id: number; keyTypeId: boolean; fetchLinkedRecords?: boolean }) => ({
          url: `/records/simple/${args.id}`,
          method: 'GET',
          params: {
            keyType: args.keyTypeId === true ? 'id' : undefined,
            fetchLinkedRecords: args.fetchLinkedRecords,
          },
        }),
        transformResponse: (response: RecordsWrapper) => createRecordData(response.result.results[0]),
        providesTags: (result, _error, { cache = true }) => {
          if (cache) {
            return result ? [{ type: 'Record' as const, id: result.original.id }] : ['Record'];
          }
          return [];
        },
      }),
      getDefaultRecordValues: builder.query<RecordStructure, GetRecordByIdApiProps>({
        query: ({ id, keyTypeId }) => `/records/default/simple/${id}${keyTypeId ? '?keyType=id' : ''}`,
        transformResponse: (response: RecordDefaultValuesWrapper) => createRecordData(response.result),
        providesTags: (result) =>
          result ? [{ type: 'DefaultRecord' as const, id: result.original.formId }] : ['DefaultRecord'],
      }),
      getRecordLinkFieldsDefaultValues: builder.query<RecordStructure, LinkedRecordDefaultValuesApiProps>({
        query: ({ recordId, fieldId }) => `/records/${recordId}/fields/${fieldId}/default/simple?keyType=id`,
        transformResponse: (response: RecordDefaultValuesWrapper) => createRecordData(response.result),
        providesTags: (result) =>
          result ? [{ type: 'DefaultRecord' as const, id: result.original.formId }] : ['DefaultRecord'],
      }),
      getRecords: builder.query<Array<RecordStructure>, GetRecordsApiProps>({
        query: (args: {
          formId: number;
          keyTypeId: boolean;
          page?: number;
          pageSize?: number;
          filters?: FieldFilters;
          sortDirective?: string;
          globalHierarchyFilters?: string;
          recordIds?: number[];
          excludedRecordId?: number;
        }) => {
          const recordIdsFilter =
            args.recordIds && args.recordIds.length > 0 ? `&filter=id:in:${args.recordIds.join(',')}` : '';

          const excludedRecordIdFilter =
            args.excludedRecordId !== undefined ? `&filter=id:neq:${args.excludedRecordId}` : '';

          return `/forms/${args.formId}/records/simple?${args.keyTypeId ? 'keyType=id&' : ''}page=${args.page}&pageSize=${args.pageSize}${createFieldFilterText({ filters: args.filters })}${args.globalHierarchyFilters ?? ''}${args.sortDirective ?? ''}${recordIdsFilter}${excludedRecordIdFilter}`;
        },
        transformResponse: (response: RecordsWrapper) => {
          return response.result.hits > 0
            ? response.result.results.filter((value) => value.fields).map((item) => createRecordData(item))
            : [];
        },
        providesTags: (result) =>
          result
            ? [
                ...result.map((item) => ({ type: 'Record', id: item.original.id }) as const),
                { type: 'Record', id: 'LIST' },
              ]
            : [{ type: 'Record', id: 'LIST' }],
      }),
      getLinkedRecords: builder.query<LinkedRecordResult[], LinkedRecordsApiProps>({
        query: (args: LinkedRecordsApiProps) =>
          `/records/${args.recordId}/fields/${args.fieldId}/linked-records?linkedRecordIds=${
            args.linkedRecordIds
          }${createFieldSortText(args.sorts)}`,
        transformResponse: (response: LinkedRecordsWrapper) => response.result ?? [],
        providesTags: (result, _error, { recordId }) =>
          result
            ? [
                ...result.map((item) => ({ type: 'LinkedRecord' as const, id: item.id })),
                { type: 'LinkedRecord', id: `LIST-${recordId}` } as const,
                'LinkedRecord',
              ]
            : [{ type: 'LinkedRecord', id: `LIST-${recordId}` } as const],
      }),
      getReverseLinkedRecords: builder.query<LinkedRecordResult[], LinkedRecordsApiProps>({
        query: (args: LinkedRecordsApiProps) =>
          `/records/${args.recordId}/fields/${args.fieldId}/reverse-linked-records?linkedRecordIds=${
            args.linkedRecordIds
          }${createFieldSortText(args.sorts)}`,
        transformResponse: (response: LinkedRecordsWrapper) => response.result ?? [],
        providesTags: (result, _error, { recordId }) =>
          result
            ? [
                ...result.map((item) => ({ type: 'ReverseLinkedRecord' as const, id: item.id })),
                { type: 'ReverseLinkedRecord', id: `LIST-${recordId}` } as const,
                'ReverseLinkedRecord',
              ]
            : [{ type: 'ReverseLinkedRecord', id: `LIST-${recordId}` } as const],
      }),
      getSummaryRecordsLinked: builder.query<Array<SummaryRecordLinkResult>, LinkedRecordsApiProps>({
        query: ({ recordId, fieldId, sorts, linkedRecordIds }: LinkedRecordsApiProps) =>
          `/records/${recordId}/fields/${fieldId}/summary-records${createSummaryLinkedRecordsFilterText(
            linkedRecordIds
          )}${createFieldSortText(sorts)}`,
        transformResponse: (response: SummaryRecordLinkResponse) => response.result,
        providesTags: (result, _error, { recordId }) =>
          result
            ? [
                ...result.map((item) => ({ type: 'SummaryLinkedRecord' as const, id: item.fieldId })),
                { type: 'SummaryLinkedRecord', id: `LIST-${recordId}` } as const,
                'SummaryLinkedRecord',
              ]
            : [{ type: 'SummaryLinkedRecord', id: `LIST-${recordId}` } as const],
      }),
      getLinkedRecordColumnValues: builder.query<Array<RecordResult>, GetLinkedRecordColumnValuesApiProps>({
        query: ({ formId, linkedRecordIds, keyTypeId = false }) => ({
          url: `/forms/${formId}/linked-record-column-values/simple`,
          method: 'GET',
          params: {
            keyType: keyTypeId === true ? 'id' : undefined,
            linkedRecordIds,
          },
        }),
      }),
      saveLinkedRecordsLayout: builder.mutation<boolean, SaveLayoutApiProps>({
        query: ({ linkedRecordsFieldId, recordId, linkedRecordIds }) => ({
          url: `/records/${recordId}/fields/${linkedRecordsFieldId}/layout`,
          method: 'POST',
          body: linkedRecordIds,
        }),
        invalidatesTags: ['LinkedRecord'],
      }),
      getRecordFieldTemplateExport: builder.query<string, RecordFieldTemplateExport>({
        query: ({ recordId, fieldId, fieldType }) => {
          return {
            url: `/records/${recordId}/fields/${fieldId}/export?fieldType=${fieldType}`,
            responseHandler: (response: Response) => {
              if (response.status === 200) {
                return response.blob();
              }

              return response.json();
            },
          };
        },
        transformResponse: (response: Blob) => {
          return fileToSrc(response);
        },
      }),
      getAssignedRecords: builder.query<Array<AssignedRecordsResult>, unknown>({
        query: (args: { filters?: FieldFilters; page?: number; pageSize?: number }) => {
          let url = '/records/important/simple';
          if (args.page && args.pageSize) {
            url += `?page=${args.page}&pageSize=${args.pageSize}`;
            if (args.filters && Object.keys(args.filters).length > 0) {
              url += `${createFieldFilterText({ filters: args?.filters })}`;
            }
          }
          return url;
        },
        providesTags: ['AssignedRecords'],
        transformResponse: (response: AssignedRecordsResponse) => response.result.results || [],
      }),
      getMyActivities: builder.query<Array<AssignedRecordItem>, MyActivitiesApiProps>({
        query: ({ filters, page, pageSize, sortDirective }) =>
          `/my-activities?page=${page}&pageSize=${pageSize}${createFieldFilterText({ filters })}${sortDirective ?? ''}`,
        providesTags: ['AssignedRecords'],
        transformResponse: (response: MyActivitiesApiResponse) => response.items || [],
      }),
      getAssignedRecordsCount: builder.query<Array<AssignedRecordsCount>, void>({
        query: () => `/records/important/simple/count`,
        providesTags: ['AssignedRecordsCount'],
        transformResponse: (response: AssignedRecordsCountResponse) => response.result || [],
      }),
      getRecordPermissions: builder.query<RecordPermissionsResult, unknown>({
        query: (args: { recordId: number }) => `/records/${args.recordId}/permissions`,
        transformResponse: (response: RecordPermissionsResponse) => response.result,
        providesTags: ['RecordPermissions'],
      }),
      updateRecord: builder.mutation<number, PatchRecordApiProps>({
        query: ({ id, patch, applyDefaultValues = false, stepAdvanced = false }) => ({
          url: `/records/simple/${id}?keyType=id&applyDefaultValues=${applyDefaultValues}${stepAdvanced ? '&stepAdvanced=true' : ''}`,
          method: 'PATCH',
          body: patch,
          validateStatus: (response, result) => response.status === 200 && result.result,
        }),
        transformResponse: (response: PatchRecordSuccessResponse) => response.result,
        invalidatesTags: (_result, error, { id }, meta) => {
          const isConflictError = error && error.status === 900; // 900 CONFLICT_DATA is a custom error code

          // Skip tag invalidation if this is a queued offline mutation
          // The queuedOffline flag is passed through meta by webViewBaseQuery
          if (meta && 'queuedOffline' in meta && meta.queuedOffline) {
            return [];
          }

          return isConflictError
            ? []
            : [
                { type: 'Record', id },
                { type: 'Record', id: 'LIST' },
                'Record',
                'LinkedRecord',
                'ReverseLinkedRecord',
                'SummaryLinkedRecord',
                'AssignedRecords',
                'AssignedRecordsCount',
                'RecordPermissions',
                'AllFormsRecord',
              ];
        },
      }),
      addRecord: builder.mutation<number, PostRecordApiProps>({
        query: ({ body, applyDefaultValues = false, parentRecordId }) => ({
          url: `/records/simple?keyType=id&applyDefaultValues=${applyDefaultValues}${parentRecordId ? `&parentRecordId=${parentRecordId}` : ''}`,
          method: 'POST',
          body: [body],
          validateStatus: (response, result) =>
            response.status === 200 && result?.result?.[0]?.success === true && result?.result?.[0]?.id !== undefined,
        }),
        transformResponse: (response: PostRecordSuccessResponse) => response.result[0].id,
        invalidatesTags: (result, _, { parentRecordId }) => {
          if (result) {
            const tags: Array<string | { type: string; id: string | number }> = [
              { type: 'Record', id: 'LIST' },
              'TotalRecordCount',
              'AssignedRecordsCount',
              'AllFormsRecord',
            ];
            if (parentRecordId) {
              tags.push({ type: 'Record', id: parentRecordId });
            }
            return tags as any;
          }
          return [];
        },
      }),
      deleteRecord: builder.mutation<DeleteRecordResponse, number>({
        query: (id: number) => ({
          url: `/records/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: [
          { type: 'Record', id: 'LIST' },
          { type: 'AllFormsRecord', id: 'LIST' },
          'LinkedRecord',
          'ReverseLinkedRecord',
          'SummaryLinkedRecord',
          'TotalRecordCount',
          'AssignedRecords',
          'AssignedRecordsCount',
        ],
      }),
      getAuditLogs: builder.query<Array<AuditLogData>, AuditLogsRequestBody>({
        query: ({ recordId, page, pageSize, sortByVersion }: AuditLogsRequestBody) => {
          let url = `/records/${recordId}/audit-logs?page=${page}&pageSize=${pageSize}`;
          if (sortByVersion === 'desc') {
            url += '&sort=-version';
          }
          return url;
        },
        transformResponse: (response: AuditLogsResponse) => transformAuditLogResponse(response.items ?? []),
      }),
      getAuditLogDetails: builder.query<Array<AuditLogDataItem>, AuditLogDetailsRequestBody>({
        query: ({ recordId, auditLogVersion }: AuditLogDetailsRequestBody) =>
          `/records/${recordId}/audit-logs/${auditLogVersion}`,
        transformResponse: (response: AuditLogDetailsResponse, _, { auditLogVersion }) =>
          transformAuditLogDetailsResponse(response.result ?? [], auditLogVersion),
      }),
      getNotificationLog: builder.query<Array<NotificationLogData>, NotificationLogParameters>({
        query: (args) => `/records/${args.recordId}/notification-log`,
        transformResponse: (response: NotificationLogResponse) => response.result || [],
      }),
      getRecordCount: builder.query<number, RecordCountApiProps>({
        query: (args) => {
          const globalHierarchyFilters = args.globalHierarchyFilters ?? '';

          const excludedRecordIdFilter =
            args.excludedRecordId !== undefined ? `&filter=id:neq:${args.excludedRecordId}` : '';

          return `/forms/${args.formId}/records/count${createFieldFilterTextAsFirstQueryParameter(args.filters)}${globalHierarchyFilters}${excludedRecordIdFilter}`;
        },
        transformResponse: (response: ResultValidationResponse<number>) => response.result,
        providesTags: ['TotalRecordCount'],
      }),
      getRecordVersions: builder.query<Array<RecordResult>, { recordId: number }>({
        query: (args) => `/records/${args.recordId}/versions`,
        transformResponse: (response: ResultValidationResponse<RecordsResult>) => response.result.results,
        providesTags: (result) =>
          result
            ? [
                ...result.map((item) => ({ type: 'RecordVersion' as const, id: item.id })),
                { type: 'RecordVersion', id: 'LIST' } as const,
              ]
            : [{ type: 'RecordVersion', id: 'LIST' } as const],
      }),
      exportRecord: builder.query<ExportRecord, ExportRecordArgs>({
        query: (args) => ({
          url: `/records/${args.recordId}/export`,
          method: 'GET',
          params: {
            format: args.format,
            appendRecordlinksAsSeparatePages: args.appendRecordlinksAsSeparatePages,
            appendSignaturePage: args.appendSignaturePage,
            sectionIds: args.sectionIds,
          },
          responseHandler: async (response: Response) => {
            if (response.status === 200) {
              const contentDisposition = response.headers.get('Content-Disposition');
              if (contentDisposition) {
                const file = await response.blob();
                if (contentDisposition.includes('inline')) {
                  return { action: 'new-tab', file };
                } else if (contentDisposition.includes('attachment')) {
                  return { action: 'download', file };
                }
              }
            } else {
              return response.json();
            }
          },
        }),
        transformResponse: (response: ExportRecordResponse) => {
          return { action: response.action, fileSource: fileToSrc(response.file) };
        },
      }),
      getExportRecordUrl: builder.query<string, ExportRecordArgs>({
        query: (args) => ({
          url: `/records/${args.recordId}/export/url`,
          method: 'GET',
          params: {
            format: args.format,
            appendRecordlinksAsSeparatePages: args.appendRecordlinksAsSeparatePages,
            appendSignaturePage: args.appendSignaturePage,
          },
          responseHandler: 'text',
        }),
      }),
      getExternalUserEmailTemplate: builder.query<ExternalUserEmailTemplate, GetExternalUserEmailTemplateArgs>({
        query: ({ type } = {}) => `/email-template/external-user/simple${type ? `?type=${type}` : ''}`,
        transformResponse: (response: ResultValidationResponse<Array<ExternalUserEmailTemplate>>) => {
          return response.result[0];
        },
      }),
      sendInvitationEmail: builder.mutation<InvitationEmailResult, ExternalEmailInvitation>({
        query: (args) => ({
          url: `/records/${args.recordId}/fields/${args.template.id}/invite`,
          method: 'POST',
          body: args.template,
        }),
        invalidatesTags: [{ type: 'Collaborator', id: 'LIST' }],
        transformResponse: (response: ResultValidationResponse<InvitationEmailResult>) => response.result,
      }),
      sendContractorInvitationEmail: builder.mutation<InvitationEmailResult, ExternalEmailInvitation>({
        query: (args) => ({
          url: `/records/${args.recordId}/invite`,
          method: 'POST',
          body: args.template,
        }),
        invalidatesTags: [{ type: 'Collaborator', id: 'LIST' }],
        transformResponse: (response: ResultValidationResponse<InvitationEmailResult>) => response.result,
      }),
      sendEmailThis: builder.mutation<boolean, EmailThisTemplate>({
        query: (args) => ({
          url: `/records/${args.recordId}/fields/${args.fieldId}/email`,
          method: 'POST',
          body: args.emailData,
        }),
        invalidatesTags: [{ type: 'Collaborator', id: 'LIST' }],
        transformResponse: (response: ResultValidationResponse<boolean>) => response.result,
      }),
      getRecordCollaborators: builder.query<Array<RecordCollaborators>, CollaboratorsRequestBody>({
        query: ({ recordId }) => ({
          url: `/records/${recordId}/collaborators`,
          method: 'GET',
        }),
        transformResponse: (response: RecordCollaboratorsWrapper) => {
          return (
            response.result?.map((collaborator) => {
              return {
                ...collaborator,
                email: collaborator.email === 'null' || collaborator.email === null ? '' : collaborator.email,
              };
            }) ?? []
          );
        },
        providesTags: (result) => providesList(result, 'Collaborator'),
      }),
      getCollaboratorEmail: builder.query<ExternalUserEmailTemplate, number | undefined>({
        query: (emailId) => `/records/collaborator-emails/${emailId}`,
        transformResponse: (response: ResultValidationResponse<ExternalUserEmailTemplate>) => {
          return response.result;
        },
      }),
      editRecordCollaborator: builder.mutation<number, ExternalUserEmailTemplate>({
        query: ({ id, ...patch }) => ({
          url: `/collaborators/${id}`,
          method: 'PUT',
          body: patch,
        }),
        invalidatesTags: [{ type: 'Collaborator', id: 'LIST' }],
      }),
      deleteRecordCollaborator: builder.mutation<number, number>({
        query: (id) => ({
          url: `/collaborators/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: [{ type: 'Collaborator', id: 'LIST' }],
      }),
      getAllFormsRecords: builder.query<RecordsResult, GetAllFormsApiProps>({
        query: (args) =>
          `/modules/${args.moduleId}/all-forms/records/simple?keyType=id&page=${args.page}&pageSize=${args.pageSize}${
            args.globalHierarchyFilters ?? ''
          }${createFieldFilterText({
            filters: args.filters,
            captionValuesBase64encoded: args.captionValuesBase64encoded,
            isAllForms: true,
          })}${args.sorts}&includeTotal=true`,
        transformResponse: (response: RecordsWrapper) => {
          return {
            ...response.result,
            results:
              response.result?.results?.map((item) => forceAssert<RecordResult>(createRecordData(item).flat)) ?? [],
          };
        },
        providesTags: (result) =>
          result && result.results
            ? [
                ...result.results.map((item) => ({ type: 'AllFormsRecord', id: item.id }) as const),
                { type: 'AllFormsRecord', id: 'LIST' },
              ]
            : [{ type: 'AllFormsRecord', id: 'LIST' }],
      }),
      deleteLatestVersion: builder.mutation<ResultValidationResponse<number>, number>({
        query: (id: number) => ({
          url: `/records/${id}/latest-version`,
          method: 'DELETE',
        }),
        invalidatesTags: [
          { type: 'Record', id: 'LIST' },
          { type: 'RecordVersion', id: 'LIST' },
        ],
      }),
      createNewVersion: builder.mutation<ResultValidationResponse<number>, number>({
        query: (id: number) => ({
          url: `/records/${id}/new-version`,
          method: 'POST',
          body: {},
        }),
        invalidatesTags: [
          { type: 'Record', id: 'LIST' },
          { type: 'RecordVersion', id: 'LIST' },
        ],
      }),
      createExportJob: builder.mutation<CreateExportJobResult, ExportRecords>({
        query: (args) => ({
          url: exportRecordURLConstruct(`/forms/${args.formId}/records/export?format=${args.format}`, args),
          method: 'POST',
        }),
      }),
      createSingleExportJob: builder.mutation<ExportSingleRecordJobResult, ExportSingleRecord>({
        query: (args) => ({
          url: `/records/${args.recordId}/export/async`,
          method: 'POST',
          params: args,
        }),
        transformResponse: (response: ResultValidationResponse<ExportSingleRecordJobResult>) => response.result,
      }),
      createAllFormsExportJob: builder.mutation<CreateExportJobResult, ExportAllFormsRecords>({
        query: (args) => ({
          url: exportRecordURLConstruct(`/modules/${args.moduleId}/records/export?format=${args.format}`, args),
          method: 'POST',
        }),
      }),
      createRecordFieldExportJob: builder.query<ExportRecordFieldJobResult, ExportRecordFieldApiProps>({
        query: (args) => `/records/${args.recordId}/fields/${args.fieldId}/export/async?fieldType=${args.format}`,
        transformResponse: (response: ResultValidationResponse<ExportRecordFieldJobResult>) => response.result,
      }),
      getMutateRecordBackgroundJobDetails: builder.query<MutateRecordBackgroundJobResult, number>({
        query: (id) => `/background-jobs/${id}`,
        transformResponse: (response: ResultValidationResponse<MutateRecordBackgroundJobResult>) => response.result,
      }),
      createImportJob: builder.mutation<CreateImportJobResult, number>({
        query: (formId) => ({
          url: `/background-jobs/import-records/${formId}`,
          method: 'POST',
        }),
        transformResponse: (response: ResultValidationResponse<CreateImportJobResult>) => response.result,
      }),
      startImportJob: builder.mutation<BackgroundJobDetailsResult, StartImportJobApiProps>({
        query: ({ id, ...rest }) => ({
          url: `/background-jobs/import-records/${id}/start`,
          params: rest,
          method: 'POST',
        }),
        transformResponse: (response: ResultValidationResponse<BackgroundJobDetailsResult>) => response.result,
      }),
      confirmImportJob: builder.mutation<BackgroundJobDetailsResult, number>({
        query: (id) => ({
          url: `/background-jobs/import-records/${id}/confirm`,
          method: 'POST',
        }),
        transformResponse: (response: ResultValidationResponse<BackgroundJobDetailsResult>) => response.result,
      }),
      getImportJobDetails: builder.query<BackgroundJobDetailsResult, number>({
        query: (id) => `/background-jobs/import-records/${id}`,
        transformResponse: (response: ResultValidationResponse<BackgroundJobDetailsResult>) => response.result,
      }),
      createRecordCloneJob: builder.mutation<CloneRecordsJobResult, CloneRecordsApiProps>({
        query: ({ recordId, fieldId, ...rest }) => ({
          url: `/records/${recordId}/fields/${fieldId}/clone-async`,
          body: rest,
          method: 'POST',
        }),
        transformResponse: (response: ResultValidationResponse<CloneRecordsJobResult>) => response.result,
      }),
      saveRecordBackgroundJob: builder.mutation<CreateBackgroundJobResult, Omit<RecordResult, 'id'>>({
        query: (args) => ({
          url: '/records/simple/async?keyType=id',
          method: 'POST',
          body: [args],
        }),
        transformResponse: (response: ResultValidationResponse<CreateBackgroundJobResult>) => response.result,
      }),
      updateRecordBackgroundJob: builder.mutation<CreateBackgroundJobResult, BackgroundJobRecordUpdateApiProps>({
        query: (args) => ({
          url: `/records/simple/async/${args.id}?keyType=id${args.stepAdvanced ? '&stepAdvanced=true' : ''}`,
          method: 'PATCH',
          body: args,
        }),
        transformResponse: (response: ResultValidationResponse<CreateBackgroundJobResult>) => response.result,
      }),
      startAnalysisJob: builder.mutation<StartAnalysisJobApiResponse, StartAnalysisJobApiArg>({
        query: (queryArg) => {
          const formData = new FormData();
          formData.append('file', queryArg.body.file, 'data.csv');

          return {
            url: `/ai/analyze/file`,
            method: 'POST',
            body: formData,
            params: {
              ...(queryArg.extraPrompt && { extra_prompt: queryArg.extraPrompt }),
              module_id: queryArg.moduleId,
              form_id: queryArg.formId,
            },
          };
        },
        transformResponse: (response: StartAnalysisJobApiResponse): StartAnalysisJobApiResponse => ({
          backgroundJobId: JSON.parse(String(response.backgroundJobId)),
          message: response.message,
        }),
      }),
      startSingleRecordAnalysisJob: builder.mutation<StartAnalysisJobApiResponse, number>({
        query: (recordId) => ({
          url: `ai/analyze/single-record`,
          method: 'POST',
          params: {
            record_id: recordId,
          },
        }),
        transformResponse: (response: StartAnalysisJobApiResponse): StartAnalysisJobApiResponse => ({
          backgroundJobId: JSON.parse(String(response.backgroundJobId)),
          message: response.message,
        }),
      }),
      getAnalysisJobStatus: builder.query<GetAnalysisJobStatusApiResponse, number>({
        query: (backgroundJobId) => `/ai/analyze/${backgroundJobId}`,
      }),
      createFullRecordDataExportJob: builder.mutation<CreateExportJobResult, ExportFullRecordsDataProps>({
        query: ({ format, formId, ...rest }) => ({
          url: exportRecordURLConstruct(`/forms/${formId}/full-records-export?format=${format}`, rest),
          method: 'POST',
          body: {
            includeAttachments: rest.includeAttachments,
            includeLinkedRecords: rest.includeLinkedRecords,
          },
        }),
        transformResponse: (response: ResultValidationResponse<CreateExportJobResult>) => response.result,
      }),
      getFieldVisibilities: builder.mutation<Record<number, boolean>, RecordFieldVisibilityApiArgs>({
        query: ({ fieldIds, record }) => ({
          url: `/records/field-visibilities`,
          method: 'POST',
          body: record,
          params: {
            keyType: 'id',
            fieldIds: fieldIds.join(','),
          },
        }),
        transformResponse: (response: ResultValidationResponse<Record<number, boolean>>) => response.result ?? {},
      }),
      getSectionVisibilities: builder.mutation<Record<number, boolean>, RecordSectionVisibilityApiArgs>({
        query: ({ sectionIds, record }) => ({
          url: `/records/section-visibilities`,
          method: 'POST',
          body: record,
          params: {
            keyType: 'id',
            sectionIds: sectionIds.join(','),
          },
        }),
        transformResponse: (response: ResultValidationResponse<Record<number, boolean>>) => response.result ?? {},
      }),
      getBackgroundJobs: builder.query<BackgroundJobsResult, BackgroundJobsApiProps>({
        query: ({ type, page = 1, pageSize = 50 }) =>
          `/background-jobs?filter=type:in:${type}&page=${page}&pageSize=${pageSize}&filter=archived:eq:false&sort_directive=-createdAt`,
        providesTags: (result) => providesList(result?.items, 'BackgroundJobs'),
      }),
      getBackgroundJobById: builder.query<BackgroundJobsItem, number>({
        query: (id) => `/background-jobs/${id}`,
        transformResponse: (response: ResultValidationResponse<BackgroundJobsItem>) => response.result,
        providesTags: (result) => (result ? [{ type: 'BackgroundJobs', id: result.id }] : []),
      }),
      deleteBackgroundJob: builder.mutation<void, number>({
        query: (id) => ({
          url: `/background-jobs/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (_result, error, id) => {
          if (!error) {
            return [{ type: 'BackgroundJobs', id }];
          }
          return [];
        },
      }),
    }),
  });

export const {
  useGetRecordByIdQuery,
  useGetDefaultRecordValuesQuery,
  useLazyGetRecordByIdQuery,
  useGetRecordsQuery,
  useLazyGetRecordsQuery,
  useGetLinkedRecordsQuery,
  useGetReverseLinkedRecordsQuery,
  useGetSummaryRecordsLinkedQuery,
  useGetLinkedRecordColumnValuesQuery,
  useLazyGetLinkedRecordColumnValuesQuery,
  useLazyGetRecordFieldTemplateExportQuery,
  useGetAssignedRecordsQuery,
  useGetMyActivitiesQuery,
  useGetAssignedRecordsCountQuery,
  useGetRecordPermissionsQuery,
  useUpdateRecordMutation,
  useAddRecordMutation,
  useDeleteRecordMutation,
  useLazyGetAuditLogsQuery,
  useLazyGetAuditLogDetailsQuery,
  useLazyGetNotificationLogQuery,
  useGetRecordCountQuery,
  useGetRecordVersionsQuery,
  useLazyExportRecordQuery,
  useLazyGetExternalUserEmailTemplateQuery,
  useSendInvitationEmailMutation,
  useSendEmailThisMutation,
  useGetRecordCollaboratorsQuery,
  useLazyGetCollaboratorEmailQuery,
  useEditRecordCollaboratorMutation,
  useDeleteRecordCollaboratorMutation,
  useGetAllFormsRecordsQuery,
  useLazyGetAllFormsRecordsQuery,
  useDeleteLatestVersionMutation,
  useCreateNewVersionMutation,
  useCreateExportJobMutation,
  useCreateSingleExportJobMutation,
  useCreateAllFormsExportJobMutation,
  useCreateRecordFieldExportJobQuery,
  useCreateImportJobMutation,
  useStartImportJobMutation,
  useGetImportJobDetailsQuery,
  useCreateRecordCloneJobMutation,
  useConfirmImportJobMutation,
  useGetRecordLinkFieldsDefaultValuesQuery,
  useSaveLinkedRecordsLayoutMutation,
  useLazyGetExportRecordUrlQuery,
  useStartAnalysisJobMutation,
  useGetAnalysisJobStatusQuery,
  useSendContractorInvitationEmailMutation,
  useGetFieldVisibilitiesMutation,
  useGetSectionVisibilitiesMutation,
  useDeleteBackgroundJobMutation,
  useGetBackgroundJobsQuery,
  useStartSingleRecordAnalysisJobMutation,
} = recordApi;
